import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Lock, Mail, Download, Trash2, AlertTriangle, Bell } from "lucide-react";

const SECTIONS = [
  { key: "profile", label: "Profile", icon: User },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "password", label: "Password", icon: Lock },
  { key: "email", label: "Email", icon: Mail },
  { key: "data", label: "Your Data", icon: Download },
  { key: "danger", label: "Delete Account", icon: Trash2 },
];

function ProfileTab() {
  const { user, refreshMe } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.put("/me/profile", { name: name.trim() });
      toast.success("Profile updated");
      await refreshMe?.();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div>
        <Label>Display name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" className="mt-2" />
      </div>
      <Button onClick={save} disabled={busy} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest" data-testid="settings-save-profile">
        Save changes
      </Button>
    </div>
  );
}

function PasswordTab() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.put("/me/password", { current_password: current, new_password: next });
      toast.success("Password changed");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Change failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div>
        <Label>Current password</Label>
        <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-2" data-testid="settings-current-password" />
      </div>
      <div>
        <Label>New password</Label>
        <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-2" data-testid="settings-new-password" />
      </div>
      <div>
        <Label>Confirm new password</Label>
        <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2" data-testid="settings-confirm-password" />
      </div>
      <Button onClick={save} disabled={busy || !current || !next} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest" data-testid="settings-save-password">
        Change password
      </Button>
    </div>
  );
}

function EmailTab() {
  const { user, refreshMe } = useAuth();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      await api.put("/me/email", { new_email: email.trim().toLowerCase(), current_password: pw });
      toast.success("Email updated");
      setEmail(""); setPw("");
      await refreshMe?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Change failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="label-mono">Current: <span className="text-foreground">{user?.email}</span></div>
      <div>
        <Label>New email</Label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" data-testid="settings-new-email" />
      </div>
      <div>
        <Label>Confirm password</Label>
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-2" data-testid="settings-email-password" />
      </div>
      <Button onClick={save} disabled={busy || !email || !pw} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest" data-testid="settings-save-email">
        Change email
      </Button>
    </div>
  );
}

function DataTab() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lanex-data-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data downloaded");
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-background p-4">
        <div className="font-display font-bold">Download all your data</div>
        <div className="mt-1 text-sm text-muted-foreground">Includes your profile, progress, certificates, code submissions, AI conversations, projects, and feedback — everything we store for you, in a portable JSON file.</div>
        <Button onClick={download} disabled={busy} className="mt-3 rounded-full font-mono text-xs uppercase tracking-widest" variant="outline" data-testid="settings-download-data">
          <Download className="mr-2 h-3.5 w-3.5" /> Download JSON
        </Button>
      </div>
    </div>
  );
}

function DangerTab() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try {
      await api.delete("/me", { data: { current_password: pw, confirm_phrase: phrase } });
      toast.success("Account scheduled for deletion");
      await logout();
      navigate("/", { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Deletion failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4">
        <div className="flex items-center gap-2 font-display font-bold text-destructive">
          <AlertTriangle className="h-4 w-4" /> Delete this account
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          Your account will be soft-deleted immediately and permanently removed after <strong>30 days</strong>. You can restore within that window by contacting support. Certificates you earned remain verifiable via their unique verification URLs.
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <Label>Confirm password</Label>
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-2" data-testid="settings-delete-password" />
          </div>
          <div>
            <Label>Type <span className="font-mono">DELETE MY ACCOUNT</span> to confirm</Label>
            <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} className="mt-2 font-mono" data-testid="settings-delete-phrase" />
          </div>
          <Button
            onClick={del}
            disabled={busy || !pw || phrase.trim().toUpperCase() !== "DELETE MY ACCOUNT"}
            className="rounded-full font-mono text-xs uppercase tracking-widest bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="settings-delete-account"
          >
            <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete account
          </Button>
        </div>
      </div>
    </div>
  );
}

function NotificationsTab() {
  const { user, refreshMe } = useAuth();
  const [prefs, setPrefs] = useState(user?.email_prefs || { learning_reminders: true });
  const [busy, setBusy] = useState(false);
  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.put("/me/email-prefs", next);
      setPrefs(data.email_prefs);
      toast.success("Preferences saved");
      await refreshMe?.();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  const toggle = (key) => save({ ...prefs, [key]: !prefs[key] });
  return (
    <div className="space-y-4">
      <div>
        <div className="font-display text-base font-bold">Learning reminders</div>
        <p className="mt-1 text-sm text-muted-foreground">Warm, no-pressure emails when you've been away — plus a Sunday evening progress digest.</p>
      </div>

      <label className="flex items-start gap-3 rounded-xl border border-border bg-background p-4 cursor-pointer" data-testid="settings-notifications-learning">
        <input
          type="checkbox"
          checked={!!prefs.learning_reminders}
          onChange={() => toggle("learning_reminders")}
          disabled={busy}
          className="mt-1 h-4 w-4 rounded border-border accent-primary"
          data-testid="settings-notifications-learning-toggle"
        />
        <div className="flex-1">
          <div className="font-medium">Learning reminders & weekly digest</div>
          <div className="mt-1 text-xs text-muted-foreground">
            • &quot;The Academy misses you&quot; email if you've been idle for 3 days (and once more at 10 days).<br />
            • Weekly recap every Sunday evening with your XP, lessons completed, badges, and next lesson.
          </div>
        </div>
      </label>

      <div className="label-mono">Unsubscribe links in every reminder email also disable this instantly.</div>
    </div>
  );
}

export default function AccountSettingsPage() {
  const [tab, setTab] = useState("profile");
  return (
    <div data-testid="settings-root" className="space-y-6 animate-fade-up">
      <div>
        <div className="label-mono text-primary">// account</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">Settings.</h1>
        <p className="mt-1 text-sm text-muted-foreground">Manage your profile, security, and data.</p>
      </div>
      <div className="grid gap-6 md:grid-cols-[220px_1fr]">
        <aside className="rounded-2xl border border-border bg-card p-2 h-fit">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setTab(s.key)}
              data-testid={`settings-tab-${s.key}`}
              className={`w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-left transition ${tab === s.key ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"} ${s.key === "danger" ? "text-destructive/70" : ""}`}
            >
              <s.icon className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </aside>
        <section className="rounded-2xl border border-border bg-card p-6">
          {tab === "profile" && <ProfileTab />}
          {tab === "notifications" && <NotificationsTab />}
          {tab === "password" && <PasswordTab />}
          {tab === "email" && <EmailTab />}
          {tab === "data" && <DataTab />}
          {tab === "danger" && <DangerTab />}
        </section>
      </div>
    </div>
  );
}
